import { api, formatApiError, ownDancerIdParam } from "./api";
import { invalidateFeedCache } from "./apiCache";
import { hasActiveCheckInElsewhere } from "./activeCheckIn";

const FEED_FORCE_REFRESH_KEY = "ln_feed_force_refresh";
const RECENT_CHECKOUT_KEY = "ln_recent_checkout_v1";
const RECENT_CHECKOUT_TTL_MS = 2 * 60_000;

export function markFeedForceRefresh() {
  try {
    sessionStorage.setItem(FEED_FORCE_REFRESH_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Peek without consuming — survives React Strict Mode remounts. */
export function shouldForceFeedRefresh() {
  try {
    return sessionStorage.getItem(FEED_FORCE_REFRESH_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearFeedForceRefresh() {
  try {
    sessionStorage.removeItem(FEED_FORCE_REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

/** @deprecated use shouldForceFeedRefresh + clearFeedForceRefresh */
export function consumeFeedForceRefresh() {
  if (!shouldForceFeedRefresh()) return false;
  clearFeedForceRefresh();
  return true;
}

export function markRecentCheckout(eventId, occurrenceDate = null) {
  if (!eventId) return;
  try {
    sessionStorage.setItem(RECENT_CHECKOUT_KEY, JSON.stringify({
      event_id: eventId,
      occurrence_date: occurrenceDate || null,
      at: Date.now(),
    }));
  } catch {
    /* ignore */
  }
}

export function getRecentCheckout() {
  try {
    const raw = sessionStorage.getItem(RECENT_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.event_id || typeof parsed.at !== "number") {
      sessionStorage.removeItem(RECENT_CHECKOUT_KEY);
      return null;
    }
    if (Date.now() - parsed.at > RECENT_CHECKOUT_TTL_MS) {
      sessionStorage.removeItem(RECENT_CHECKOUT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearRecentCheckout() {
  try {
    sessionStorage.removeItem(RECENT_CHECKOUT_KEY);
  } catch {
    /* ignore */
  }
}

function entryMatchesCheckout(item, receipt) {
  if (!item || !receipt || item.event_id !== receipt.event_id) return false;
  if (
    receipt.occurrence_date
    && item.occurrence_date
    && item.occurrence_date !== receipt.occurrence_date
  ) {
    return false;
  }
  return true;
}

function stripCheckedInOnEntry(item) {
  if (!item) return item;
  const next = {
    ...item,
    checked_in_at: null,
    checked_out: true,
    can_check_in: false,
  };
  if (item.my_rsvp) {
    next.my_rsvp = { ...item.my_rsvp, checked_in_at: null };
  }
  return next;
}

/** Hide checked-in banner/state for an event we just checked out of (stale feed race). */
export function applyRecentCheckoutToFeed(feed) {
  const receipt = getRecentCheckout();
  if (!feed || !receipt) return feed;

  const mapBucket = (items = []) => items.map((item) => (
    entryMatchesCheckout(item, receipt) ? stripCheckedInOnEntry(item) : item
  ));

  return {
    ...feed,
    // A stale pre-checkout response must not put the event back in either list.
    check_in: (feed.check_in || []).filter((item) => !entryMatchesCheckout(item, receipt)),
    your_nights: (feed.your_nights || []).filter((item) => !entryMatchesCheckout(item, receipt)),
    vote_pending: mapBucket(feed.vote_pending),
  };
}

/**
 * Drop the checkout receipt once an authoritative feed confirms the guest is no longer checked in.
 */
export function reconcileRecentCheckoutWithFeed(feed, { authoritative = false } = {}) {
  if (!authoritative || !feed) return;
  const receipt = getRecentCheckout();
  if (!receipt) return;

  const stillCheckedIn = [...(feed.check_in || []), ...(feed.your_nights || [])].some((item) => {
    if (!entryMatchesCheckout(item, receipt)) return false;
    return Boolean(item.checked_in_at || item.my_rsvp?.checked_in_at);
  });

  if (!stillCheckedIn) clearRecentCheckout();
}

export function canShowCheckIn({ event, myRsvp }) {
  if (!event || !myRsvp || !event.is_live) return false;
  const status = myRsvp.attendance_status || "likely";
  return status === "likely" || status === "maybe";
}

export function canOfferCheckIn({ event, myRsvp, activeCheckIn = null }) {
  if (!canShowCheckIn({ event, myRsvp })) return false;
  if (myRsvp.checked_in_at) return false;
  if (myRsvp.floor_attended_at || myRsvp.checked_out) return false;
  const occurrenceDate = event.selected_occurrence || myRsvp.occurrence_date;
  return !hasActiveCheckInElsewhere(activeCheckIn, event.id, occurrenceDate);
}

export async function checkInToEvent(eventId, { dancerId, occurrenceDate }) {
  const body = {
    dancer_id: ownDancerIdParam(dancerId),
    occurrence_date: occurrenceDate,
  };
  const r = await api.post(`/events/${eventId}/check-in`, body);
  clearRecentCheckout();
  await invalidateFeedCache();
  markFeedForceRefresh();
  return r.data;
}

export async function checkOutFromEvent(eventId, { dancerId, occurrenceDate }) {
  const r = await api.post(`/events/${eventId}/check-out`, {
    dancer_id: ownDancerIdParam(dancerId),
    occurrence_date: occurrenceDate,
  });
  markRecentCheckout(eventId, occurrenceDate);
  await invalidateFeedCache();
  markFeedForceRefresh();
  return r.data;
}

/** @deprecated alias — prefer checkOutFromEvent */
export async function undoCheckIn(eventId, opts) {
  return checkOutFromEvent(eventId, opts);
}

export function checkInErrorMessage(err) {
  return formatApiError(err);
}
